import { useToast } from "@/hooks/use-toast";
import type { Vendor, OperationsTask, SlaConfig } from "@shared/schema";



// ── Helpers ───────────────────────────────────────────────────────────────────

export function formatCents(cents: number | null | undefined) {
  if (!cents) return "$0.00";
  return `$${(cents / 100).toFixed(2)}`;
}


export function formatDate(val: string | Date | null | undefined) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString();
}


// Task #1787 — vendor chat bubbles show a combined date + time, matching the
// host-facing Messages page (Task #1783). Includes the year for messages from
// a previous year.
export function formatDateTime(val: string | Date | null | undefined) {
  if (!val) return "";
  const dt = typeof val === "string" ? new Date(val) : val;
  const now = new Date();
  const sameYear = dt.getFullYear() === now.getFullYear();
  const dateStr = dt.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const timeStr = dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${dateStr}, ${timeStr}`;
}


export const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  settled: "bg-green-100 text-green-800",
  paid: "bg-green-100 text-green-800",
  approved: "bg-purple-100 text-purple-800",
};

export const padpulseLogo = "/favicon-192x192.png";


export interface VendorProfile extends Omit<Vendor, "notifyInAppFormSent"> {
  isOwner?: boolean;
  vendorTeamMemberId?: number | null;
  teamMemberName?: string | null;
  smsTaskAssigned?: boolean;
  smsTaskMessage?: boolean;
  smsFormMessage?: boolean;
  emailTaskAssigned?: boolean;
  emailTaskMessage?: boolean;
  emailFormMessage?: boolean;
  emailPayoutStatus?: boolean;
  notifyInAppFormSent?: boolean;
}


export interface VendorSession {
  vendorId: number;
  name: string;
  businessName?: string | null;
  vendorTeamMemberId?: number | null;
  isOwner?: boolean;
  isAdmin?: boolean;
  memberRole?: "owner" | "admin" | "member";
  teamMemberName?: string | null;
  needsWelcome?: boolean;
  isMarketplaceBlocked?: boolean;
}


export interface TeamMember {
  id: number;
  vendorId: number;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string | null;
  role: "owner" | "admin" | "member";
  status: "active" | "invited" | "removed";
  invitedAt?: string | null;
  acceptedAt?: string | null;
  smsTaskAssigned?: boolean;
  smsTaskMessage?: boolean;
  // Task #2783 — per-member rates managed in the Pay tab.
  hourlyRateCents?: number | null;
  defaultTravelFeeCents?: number | null;
  flatRateOfferings?: Array<{ name: string; priceCents: number }> | null;
}


export interface HostCRM {
  hostId: number;
  hostName: string;
  hostEmail: string | null;
  hostPhone: string | null;
  businessName: string | null;
  totalJobs: number;
  lastJobDate: string | null;
  openTaskCount: number;
}


export interface PaymentTransaction {
  id: number;
  taskId?: number | null;
  propertyId?: number | null;
  propertyName?: string | null;
  propertyAddress?: string | null;
  vendorAmountCents: number;
  stripeFeeCents?: number | null;
  hostTotalCents?: number | null;
  status?: string | null;
  createdAt?: string | null;
  settledAt?: string | null;
}


export interface ServiceFormLink {
  id: number;
  token: string;
  vendorId?: number | null;
  recipientName?: string | null;
  recipientPhone?: string | null;
  propertyId?: number | null;
  propertyName?: string | null;
  propertyAddress?: string | null;
  templateId?: number | null;
  templateName?: string | null;
  templateFormType?: string | null;
  sentAt?: string | null;
}


export interface SubmissionMessage {
  id: number;
  submissionId: number;
  senderType: string;
  body: string;
  createdAt: string;
}


// ── Vendor-portal in-app link router ─────────────────────────────────────────
// Server-emitted notification links (and many SMS deep-links) target back
// into /vendor-portal with query params like ?tab=payouts, ?task=123, or
// ?form=42. When the vendor is *already* inside the portal, just calling
// wouter `navigate()` with a same-route URL only updates the search string —
// VendorDashboard's `useEffect` reads search params on mount, so the active
// tab / open task / open form will not change. This helper applies those
// query params to the dashboard's local state directly so the bell and the
// Alerts tab can route the vendor where the link intends them to land.
export type VendorPortalLinkApplier = (link: string) => boolean;


// ── Dashboard ─────────────────────────────────────────────────────────────────

export type EnrichedTask = OperationsTask & {
  assignedTeamMemberName?: string | null;
  readOnly?: boolean;
  slaConfig?: SlaConfig | null;
  propertyName?: string | null;
  propertyAddress?: string | null;
  // Task #4171 — server-enriched batch grouping metadata so the vendor portal
  // can render dispatched batch jobs under one "Batch #N" header.
  batchNumber?: number | null;
  batchTitle?: string | null;
  batchStatus?: string | null;
  // Task #2783 — assigned member's saved rates for accept-dialog auto-fill.
  assignedMemberHourlyRateCents?: number | null;
  assignedMemberTravelFeeCents?: number | null;
  assignedMemberFlatRateOfferings?: Array<{ name: string; priceCents: number }> | null;
  // Task #4315 — owner-approval state for vendor-submitted quotes.
  ownerApprovalStatus?: string | null;
  ownerApprovalNote?: string | null;
  // Forms-on-tasks — set when the task carries a form template (form-task).
  formTemplateName?: string | null;
  formSubmissionCount?: number;
};


export const ACTIVE_TASK_STATUSES = ["accepted", "in_progress", "completed", "settled", "paid", "approved"];


// ── Task Card ──────────────────────────────────────────────────────────────────

export type ToastFn = ReturnType<typeof useToast>["toast"];
